import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * VerificationGuard - Global guard that handles multi-level access control
 * 
 * REGISTRATION FLOW:
 * 1. Register (email + password)
 * 2. Verify Email
 * 3. Fill Basic Info (firstName, lastName, birthdate, gender)
 * 4. Account Under Review (waiting for admin approval)
 * 5. Account Approved → Limited access
 * 
 * ACCESS LEVELS:
 * 1. Not logged in → Public pages only
 * 2. Email NOT verified → /verify-email
 * 3. Email verified, basic info NOT submitted → /basic-info
 * 4. Basic info submitted, account NOT approved → /account-pending
 * 5. Account approved → Full access (premium features gated separately)
 * 
 * CRITICAL RULES:
 * 1. NEVER use navigate() or window.location inside useEffect for guards
 * 2. ALL redirects must be declarative <Navigate /> in render
 * 3. useEffect should ONLY set state, nothing else
 * 4. Wait for auth loading before making any decisions
 * 5. Check basic info BEFORE checking pending status (so users land on correct page)
 */

// Pages that don't require any authentication
const publicPaths = [
  '/login',
  '/register',
  '/verify-email',
  '/basic-info',
  '/account-pending',
  '/complete-profile',
  '/verification-pending',
  '/forgot-password',
  '/reset-password',
  '/about',
  '/privacy',
  '/terms',
  '/contact',
  '/help',
  '/pricing',
  '/donation',
  '/payment',
  '/payment-success',
  '/shop',
  '/'  // Homepage is public
];

// Required fields for mandatory profile completion after account approval
const REQUIRED_PROFILE_FIELDS = ['firstName', 'lastName', 'image', 'country', 'about'];

// Check if user has completed mandatory profile fields
const hasCompletedMandatoryProfile = (user) => {
  if (!user) return false;
  
  // Check each required field
  for (const field of REQUIRED_PROFILE_FIELDS) {
    if (!user[field] || user[field] === '') {
      return false;
    }
  }
  return true;
};

const VerificationGuard = ({ children }) => {
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const location = useLocation();
  
  // ⚠️ IMPORTANT: Start with checking=true to prevent render slip on fast mobile refreshes
  const [checking, setChecking] = useState(true);

  // Simple effect to mark checking complete once auth is loaded
  useEffect(() => {
    if (!authLoading) {
      setChecking(false);
    }
  }, [authLoading]);

  // ========================================
  // ALL REDIRECT LOGIC IS IN RENDER (declarative)
  // ========================================

  const currentPath = location.pathname;
  
  // Pages that don't require ANY auth check (even for logged-in users)
  const authFreePaths = ['/login', '/register', '/verify-email', '/forgot-password', '/reset-password'];
  const isAuthFreePath = authFreePaths.some(path => currentPath.startsWith(path));
  
  // Pages allowed for pending/unapproved users
  const pendingAllowedPaths = ['/account-pending', '/basic-info', '/logout'];
  const isPendingAllowedPath = pendingAllowedPaths.some(path => currentPath.startsWith(path));
  
  // Pages allowed for users who need to complete mandatory profile
  const profileCompletionAllowedPaths = ['/edit-profile', '/logout'];
  const isProfileCompletionPath = profileCompletionAllowedPaths.some(path => currentPath.startsWith(path));
  
  // Public pages (visible to everyone)
  const isPublicPath = publicPaths.some(path => 
    currentPath === path || (path !== '/' && currentPath.startsWith(path))
  );

  // 1️⃣ Wait for auth to load
  if (authLoading || checking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mint-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // 2️⃣ Auth-free paths (login, register, etc.) - always allow
  if (isAuthFreePath) {
    return <>{children}</>;
  }

  // 3️⃣ Public paths — always allow regardless of auth/profile status
  if (isPublicPath) {
    return <>{children}</>;
  }

  // 4️⃣ For authenticated users - check registration flow completion
  if (isAuthenticated && !isPendingAllowedPath) {
    // First check if basic info is complete (has firstName)
    // This should come BEFORE checking pending status
    if (!user?.firstName && !user?.isAdmin) {
      // User hasn't completed basic info yet - redirect to basic-info
      return <Navigate to="/basic-info" replace />;
    }
    
    // Then check if account is pending approval
    if (user?.accountStatus === 'pending' || user?.accountStatus === 'rejected' || user?.accountApproved === false) {
      // User has pending/rejected account - redirect to account-pending
      return <Navigate to="/account-pending" replace />;
    }
  }

  // 5️⃣ Not authenticated → redirect to login (declarative)
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 6️⃣ User is admin → bypass all checks
  if (user?.isAdmin === true) {
    return <>{children}</>;
  }

  // 7️⃣ Check email verification
  if (!user?.isEmailVerified) {
    return <Navigate to="/verify-email" state={{ email: user?.email }} replace />;
  }

  // 8️⃣ Check if basic info submitted (has firstName) - redundant check for safety
  if (!user?.firstName) {
    return <Navigate to="/basic-info" replace />;
  }

  // 9️⃣ Check account approval status
  // First check if account status is pending or rejected - always redirect
  if (user?.accountStatus === 'pending' || user?.accountStatus === 'rejected') {
    return <Navigate to="/account-pending" replace />;
  }
  
  // Check if accountApproved is explicitly false (not approved yet)
  if (user?.accountApproved === false) {
    // Account not yet approved by admin
    return <Navigate to="/account-pending" replace />;
  }
  
  // For backward compatibility: if accountApproved is undefined/null but user has firstName,
  // treat them as approved (legacy users before the new registration flow)
  // If accountApproved is true, user is explicitly approved

  // 10️⃣ Account is approved - check if mandatory profile fields are complete
  // Required: firstName, lastName, image, country, province, dob, about, motherTongue
  if (!hasCompletedMandatoryProfile(user) && !isProfileCompletionPath) {
    // Redirect to edit-profile to complete mandatory fields
    return <Navigate to="/edit-profile?mandatory=true" replace />;
  }
  
  // 11️⃣ Account is approved and profile complete - user has LIMITED access
  // They can browse but profiles will be blurred (handled in ProfileDetailPage)
  // Full verification (badge) is optional and done in Account Settings
  
  // Allow access to all pages - profile blurring is handled at component level
  return <>{children}</>;
};

export default VerificationGuard;
